// Bitcoin address distribution — reference data for computing "your
// stack puts you in the top X% of holders."
//
// Source: BitInfoCharts / Blockchain.com consolidated data.
// This changes slowly (fractions of a percent per month) because the
// on-chain supply distribution only shifts meaningfully over halving
// cycles. We bake in a static snapshot and display "as of [date]" so
// the user knows it's a reference, not real-time.
//
// Last updated: 2025-05-01 (from BitInfoCharts "Bitcoin Rich List")
//
// Each entry: { min, max, addresses }
// min/max are BTC amounts defining the bucket.
// addresses = number of non-zero addresses in that range.
//
// We only need the cumulative "addresses holding ≥ X BTC" for the
// rank calc, but storing the full distribution lets us show context
// like "there are only 150K addresses holding ≥ 1 BTC".

export const DISTRIBUTION_DATE = '2025-05-01';

// Buckets from BitInfoCharts (rounded to avoid false precision)
export const DISTRIBUTION = [
  { min: 0,       max: 0.001,    addresses: 28_800_000 },
  { min: 0.001,   max: 0.01,     addresses: 14_200_000 },
  { min: 0.01,    max: 0.1,      addresses:  9_500_000 },
  { min: 0.1,     max: 1,        addresses:  4_200_000 },
  { min: 1,       max: 10,       addresses:    900_000 },
  { min: 10,      max: 100,      addresses:    150_000 },
  { min: 100,     max: 1000,     addresses:     16_000 },
  { min: 1000,    max: 10000,    addresses:      2_100 },
  { min: 10000,   max: 100000,   addresses:        110 },
  { min: 100000,  max: Infinity, addresses:          4 },
];

export const TOTAL_ADDRESSES = DISTRIBUTION.reduce((s, b) => s + b.addresses, 0);

// Given a BTC amount, return { rank, percentile, addressesAbove,
// totalAddresses, bucket }. "rank" = "top X%" where X < 1% is rare.
export function computeRank(btcAmount) {
  if (!Number.isFinite(btcAmount) || btcAmount <= 0) return null;

  // Count addresses holding MORE than the user's amount.
  let addressesAbove = 0;
  let userBucket = null;

  for (let i = DISTRIBUTION.length - 1; i >= 0; i--) {
    const b = DISTRIBUTION[i];
    if (btcAmount < b.min) {
      addressesAbove += b.addresses;
    } else if (btcAmount >= b.min && btcAmount < b.max) {
      userBucket = b;
      // Estimate within-bucket rank: assume uniform distribution
      // within the bucket. This is a simplification but avoids
      // claiming false precision.
      const bucketFraction = b.max === Infinity
        ? 0.5
        : (btcAmount - b.min) / (b.max - b.min);
      const estimatedAboveInBucket = Math.round(b.addresses * (1 - bucketFraction));
      addressesAbove += estimatedAboveInBucket;
    }
    // Buckets below the user's amount: skip (they hold less)
  }

  // "Top X%" = what fraction of all addresses hold at least as much
  // Include the user's own position: addresses above + 1 (the user)
  const rankPosition = addressesAbove + 1;
  const percentile = (rankPosition / TOTAL_ADDRESSES) * 100;

  return {
    percentile,
    addressesAbove,
    totalAddresses: TOTAL_ADDRESSES,
    bucket: userBucket,
    distributionDate: DISTRIBUTION_DATE,
  };
}
